// Turns a failure from the appointment write path into a sentence a therapist can act on.
//
// WHY THIS IS ITS OWN MODULE
// Since 2026-09-02 every "New Appointment" died with a bare 42501 and the UI said
// "Failed to save appointment." — a message that is true, actionable by nobody, and
// indistinguishable from a network blip. It took a database audit to find out what was
// wrong. So the mapping lives here: pure, dependency-free (no React, no MUI, no supabase
// client), so it can be unit-tested without a single mock and reused by any caller.
//
// WHERE THE CODES COME FROM
// Writes now go through the SECURITY DEFINER RPCs in
// step8_appointments_therapist_write_rpcs.sql (create/update/cancel_therapist_appointment).
// supabase-js surfaces a PostgrestError as { message, details, hint, code }, where `code`
// is the SQLSTATE the function RAISEd:
//   42501  caller is not a team member / the therapist is not in the team / no access
//   22023  team and therapist are required
//   P0001  a business rule refused the write; the rule is identified by the MESSAGE text,
//          which is why the checks below match on substrings and not on the code alone
// The remaining codes are plain Postgres ones the table can still produce.
//
// The paid-appointment cases are the ones that matter most: a paid row's credits are
// returned by POST /stripe/appointments/:id/cancel-refund and by nothing else, so the
// message has to point the therapist at the client's booking rather than let them keep
// retrying a write the database will always refuse.

// Both callers are one function away from each other, so the generic branches have to know
// which verb to use: telling a therapist whose CANCEL failed that they "don't have permission
// to create appointments" sends them to look for the wrong problem.
const PERMISSION_MESSAGE = {
  save: "You don't have permission to create appointments here — ask an admin to check your team membership.",
  cancel: "You don't have permission to cancel this appointment — ask an admin to check your team membership.",
};

// The message the backend returns (HTTP 422, refund_service.py) when /cancel-refund is
// asked to refund an appointment that was never paid. Seeing it means the caller took the
// paid branch for an unpaid row, so say what the therapist should do rather than echo it.
const NO_PAYMENT_TO_REFUND_MESSAGE =
  'This appointment was booked and paid in the client’s app. Cancel it from the client’s booking so their credits are returned.';

const CONNECTION_MESSAGE = {
  save: "Couldn't reach the server. Check your connection and try again — nothing was saved.",
  cancel: "Couldn't reach the server. Check your connection and try again — the appointment was not cancelled.",
};

// Read the human-readable text off whatever we were handed. A PostgrestError, a plain
// Error thrown by fetch, and the `{ error: '...' }` body of a backend 4xx all reach this
// function, and none of them is an instance of the others.
const textOf = (error) => {
  if (!error) return '';
  if (typeof error === 'string') return error;
  return [error.message, error.details, error.error]
    .filter((part) => typeof part === 'string' && part)
    .join(' ');
};

const codeOf = (error) => {
  if (!error || typeof error === 'string') return '';
  return error.code ?? error.status ?? '';
};

// `action` only changes the generic branches — the three business-rule messages below are
// about the appointment itself and read correctly from either caller.
export function appointmentErrorMessage(error, { action = 'save' } = {}) {
  const verb = action === 'cancel' ? 'cancel' : 'save';
  if (!error) return 'Something went wrong. Please try again.';

  const message = textOf(error);
  const lower = message.toLowerCase();
  const code = String(codeOf(error));

  // Checked before the SQLSTATE switch: this one arrives from the Stripe backend as a
  // plain Error with no code, and it is the single most misleading failure in this flow.
  if (lower.includes('no completed payment to refund')) {
    return NO_PAYMENT_TO_REFUND_MESSAGE;
  }

  // The browser's offline/DNS/CORS failure is a TypeError('Failed to fetch') — no code,
  // and nothing was written, which is the part the therapist needs to know.
  if (
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('network request failed') ||
    lower.includes('load failed')
  ) {
    return CONNECTION_MESSAGE[verb];
  }

  // Link validation also uses 42501. It must not send the therapist to fix team
  // membership when the server could not safely verify the selected client.
  if (verb === 'save' && code === '42501') {
    const validationMessages = {
      'client email could not be verified': "The selected client's email could not be verified. Nothing was saved. Ask an administrator to check the client record.",
      'client identity is ambiguous': "The selected client's account could not be uniquely verified. Nothing was saved. Ask an administrator to check the client record.",
      'client link could not be verified': 'A MindVault account link could not be verified for this client. Nothing was saved.',
      'selected therapist is not available in this team': 'The selected therapist is not available in this team. Refresh the page and select a therapist again.',
      'selected client is not in this team': 'The selected client is not available in this team. Refresh the page and select a client again.',
      'selected therapist or client account could not be verified': 'The selected therapist or client account could not be verified. Nothing was saved.',
    };
    const serverMessage = typeof error.message === 'string' ? error.message.trim().toLowerCase() : lower;
    if (validationMessages[serverMessage]) return validationMessages[serverMessage];
  }

  if (code === '42501' || lower.includes('permission denied')) {
    return PERMISSION_MESSAGE[verb];
  }

  // 22007 is what saveAppointment synthesises for a cleared or half-typed date picker; the
  // message it carries already names the field, so pass it through rather than genericise it.
  if (code === '22007') {
    return textOf(error) || 'Pick a valid date and time for the appointment.';
  }

  if (code === '22023') {
    return 'Pick a therapist before saving — the appointment needs a therapist and a team.';
  }

  if (code === '23505') {
    return 'That appointment already exists — refresh the calendar before creating it again.';
  }

  if (code === '23503') {
    return 'That therapist or team no longer exists. Refresh the page and pick a therapist again.';
  }

  if (code === '22P02') {
    return 'That session type is not one this clinic supports. Pick In Person or Virtual and try again.';
  }

  if (code === '23P01') {
    if (lower.includes('not verified')) return 'Google Calendar availability has not been verified for this time. Reconnect the calendar or wait for synchronization before booking.';
    if (lower.includes('working hours')) return 'Choose a time within the therapist’s working hours. Check Manage availability for their schedule.';
    if (lower.includes('unavailable')) return 'The therapist is unavailable at this time. Choose another time and check their calendar.';
  }

  // P0001 = the RPCs' own business rules. Match the MESSAGE, because one code covers
  // three different refusals, and each one sends the therapist somewhere different.
  if (lower.includes('paid for by the client')) {
    return NO_PAYMENT_TO_REFUND_MESSAGE;
  }

  if (lower.includes('already cancelled')) {
    return 'This appointment is already cancelled.';
  }

  if (lower.includes('only therapist-created appointments')) {
    return 'This booking came from the client’s app and may still be mid-payment, so it can only be changed from the client’s booking.';
  }

  // Unknown: never swallow it. Whatever is printed here is what ends up pasted into a bug
  // report, so the raw message and SQLSTATE have to survive — losing them is exactly how
  // the 42501 outage stayed invisible for three weeks.
  const detail = [message, code && `code ${code}`].filter(Boolean).join(' — ');
  const what = verb === 'cancel' ? 'cancel the appointment' : 'save the appointment';
  return detail
    ? `Couldn't ${what}: ${detail}`
    : `Couldn't ${what}. Please try again.`;
}

export default appointmentErrorMessage;
